import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BugReportIcon from "@mui/icons-material/BugReport";
import HistoryIcon from "@mui/icons-material/History";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Swal from "sweetalert2";

import { VoiceRecorder } from "./VoiceRecorder";
import { ScreenshotUploader } from "./ScreenshotUploader";
import { MyTicketList } from "./MyTicketList";
import { useCreateUserReport } from "@/services-react-query/queries/user-report.query";
import { UserReportType } from "@/common/types/user-report";

interface UserReportModalProps {
  open: boolean;
  onClose: () => void;
}

export const UserReportModal: React.FC<UserReportModalProps> = ({ open, onClose }) => {
  const [tabIndex, setTabIndex] = useState<number>(0);

  // Form State
  const [reportType, setReportType] = useState<UserReportType>(UserReportType.BUG_TECHNICAL);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number>(0);

  const createReportMutation = useCreateUserReport();

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setImages([]);
    setAudioBlob(null);
    setAudioDuration(0);
    setReportType(UserReportType.BUG_TECHNICAL);
  };

  const handleClose = () => {
    if (!createReportMutation.isLoading) {
      onClose();
    }
  };

  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let browser = "Other";
    if (ua.indexOf("Firefox") > -1) browser = "Firefox";
    else if (ua.indexOf("SamsungBrowser") > -1) browser = "Samsung Internet";
    else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) browser = "Opera";
    else if (ua.indexOf("Trident") > -1) browser = "Internet Explorer";
    else if (ua.indexOf("Edge") > -1 || ua.indexOf("Edg") > -1) browser = "Edge";
    else if (ua.indexOf("Chrome") > -1) browser = "Chrome";
    else if (ua.indexOf("Safari") > -1) browser = "Safari";

    let os = "Other";
    if (ua.indexOf("Win") !== -1) os = "Windows";
    else if (ua.indexOf("Mac") !== -1) os = "MacOS";
    else if (ua.indexOf("Linux") !== -1) os = "Linux";
    else if (ua.indexOf("Android") !== -1) os = "Android";
    else if (ua.indexOf("like Mac") !== -1) os = "iOS";

    return {
      userAgent: ua,
      browser,
      os,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio || 1,
      language: navigator.language || "N/A",
      timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || "N/A",
      platform: navigator.platform || "N/A",
      colorDepth: `${window.screen.colorDepth}-bit`,
      online: navigator.onLine ? "Online" : "Offline",
      appVersion: "1.0.0",
    };
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Thiếu thông tin",
        text: "Vui lòng nhập tóm tắt vấn đề bạn gặp phải!",
      });
      return;
    }

    if (!description.trim() && !audioBlob) {
      Swal.fire({
        icon: "warning",
        title: "Thiếu mô tả",
        text: "Vui lòng nhập mô tả chi tiết hoặc ghi âm đoạn voice giải thích!",
      });
      return;
    }

    try {
      await createReportMutation.mutateAsync({
        type: reportType,
        title: title.trim(),
        description: description.trim() || "(Mô tả qua ghi âm voice)",
        pageUrl: window.location.href,
        deviceInfo: getDeviceInfo(),
        images,
        audioBlob: audioBlob || undefined,
        audioDuration: audioDuration || 0,
      });

      Swal.fire({
        icon: "success",
        title: "Gửi báo cáo thành công!",
        text: "Đội ngũ FongFluency đã tiếp nhận và sẽ kiểm tra sớm nhất có thể. Cảm ơn bạn!",
        confirmButtonColor: "#0D9488",
      });

      resetForm();
      setTabIndex(1); // Chuyển sang tab Lịch sử để user thấy ticket của mình
    } catch (err: any) {
      console.error("Failed to submit bug report:", err);
      Swal.fire({
        icon: "error",
        title: "Gửi thất bại",
        text: err?.response?.data?.message || "Đã xảy ra lỗi trong quá trình gửi. Vui lòng thử lại!",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundImage: "none",
          width: { xs: "95%", sm: "650px", md: "760px" },
          maxWidth: "800px",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 2,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <BugReportIcon color="primary" />
          <Typography variant="h6" fontWeight={800}>
            Trợ Giúp & Báo Cáo Sự Cố
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={createReportMutation.isLoading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, newVal) => setTabIndex(newVal)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            label="Gửi báo cáo mới"
            icon={<BugReportIcon fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "none", fontWeight: 700, minHeight: 48 }}
          />
          <Tab
            label="Lịch sử báo cáo"
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "none", fontWeight: 700, minHeight: 48 }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 2.5, maxHeight: "70vh", overflowY: "auto" }}>
        {tabIndex === 0 ? (
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Loại sự cố */}
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>
                Loại phản ánh:
              </FormLabel>
              <RadioGroup row value={reportType} onChange={(e) => setReportType(e.target.value as UserReportType)}>
                <FormControlLabel
                  value={UserReportType.BUG_TECHNICAL}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      Lỗi kỹ thuật / Bug
                    </Typography>
                  }
                />
                <FormControlLabel
                  value={UserReportType.AI_CONTENT}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      Lỗi nội dung / Bài học
                    </Typography>
                  }
                />
                <FormControlLabel
                  value={UserReportType.FEATURE_REQUEST}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      Góp ý tính năng
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* Tiêu đề */}
            <TextField
              label="Tóm tắt vấn đề"
              placeholder="VD: Không phát được âm thanh bài nghe IELTS Part 1"
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />

            {/* Mô tả chi tiết */}
            <TextField
              label="Mô tả chi tiết sự cố"
              placeholder="Mô tả các bước bạn đã làm trước khi gặp lỗi..."
              fullWidth
              multiline
              rows={3}
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            {/* Uploader ảnh */}
            <ScreenshotUploader images={images} onChangeImages={(files) => setImages(files)} maxImages={4} />

            {/* Ghi âm voice 2 phút */}
            <VoiceRecorder
              maxDurationSeconds={120}
              onAudioRecorded={(blob, duration) => {
                setAudioBlob(blob);
                setAudioDuration(duration);
              }}
            />

            {/* Thông tin tự động đính kèm */}
            <Box
              display="flex"
              alignItems="flex-start"
              gap={1}
              p={1.5}
              borderRadius={2}
              sx={{ backgroundColor: "action.hover" }}
            >
              <InfoOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.2 }} />
              <Typography variant="caption" color="text.secondary" lineHeight={1.4}>
                Hệ thống sẽ tự động đính kèm URL trang bạn đang xem và thông tin trình duyệt để đội ngũ kỹ thuật tiện
                kiểm tra và sửa lỗi.
              </Typography>
            </Box>
          </Box>
        ) : (
          <MyTicketList />
        )}
      </DialogContent>

      {tabIndex === 0 && (
        <DialogActions sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={createReportMutation.isLoading}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Hủy
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={createReportMutation.isLoading}
            startIcon={createReportMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              px: 3,
            }}
          >
            {createReportMutation.isLoading ? "Đang gửi..." : "Gửi Báo Cáo Sự Cố"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
